import Separator from "@/v2-ui/separator"
import StatusKpis from "@/v2-status-client/status/kpis/StatusKpis"
import StatusPlatformList from "@/v2-status-client/status/platform/StatusPlatformList"
import StatusPlatformNav from "@/v2-status-client/status/platform/StatusPlatformNav"
// import StatusProviderList from "@/v2-status-client/status/provider/StatusProviderList"
import StatusIncidentList from "@/v2-status-client/status/incident/StatusIncidentList"
import StatusFooter from "@/v2-status-client/status/footer/StatusFooter"
import StatusError from "@/v2-status-client/status/fallback/StatusError"
import StatusPageSkeleton from "@/v2-status-client/status/skeleton/StatusPageSkeleton"
import StatusPageHeader from "@/v2-status-client/status/StatusPageHeader"
import StatusStickyNav from "@/v2-status-client/status/nav/StatusStickyNav"
import { useStatusQuery } from "@/v2-status-client/status/status.query"
import {
  useActiveCategoryId,
  useScrollToHashWhenReady,
  useStatusFilter,
} from "@/v2-status-client/status/status.filter"

// Top-level page. Responsibilities:
//   1. Own the polling lifecycle (via useStatusQuery).
//   2. Read the active platform filter and active category hash
//      from the URL so deep-links land on the right scope without
//      an extra render.
//   3. Pick which "phase" to render: skeleton (no data yet,
//      first request still in flight), error fallback (no data
//      and the last try failed), or the real page once data is
//      in. The page chrome (header / footer) renders in every
//      phase so the brand strip and the polling pip are always
//      on screen, including while the first request is in
//      flight.
//
// Category-level navigation lives inside the per-platform detail
// view as a left sidebar (`StatusCategorySidebar`) rather than a
// secondary horizontal pill row at the top — a sidebar reads
// better when there are sub-categories underneath, and frees up
// vertical space at the top of the page for actual status data.
function StatusPage() {
  const { data, loading, isRefreshing, error, lastSuccessAt, refetch } =
    useStatusQuery()
  const { platformId, setPlatformId } = useStatusFilter()
  const activeCategoryId = useActiveCategoryId()
  useScrollToHashWhenReady(data != null)

  return (
    <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6 text-foreground">
      <StatusPageHeader />

      {!data && loading && <StatusPageSkeleton />}
      {!data && !loading && (
        <StatusError
          error={error}
          isRefreshing={isRefreshing}
          onRetry={refetch}
        />
      )}

      {data && (
        <>
          <StatusKpis summary={data.summary} />
          <StatusStickyNav>
            <StatusPlatformNav
              platforms={data.platforms}
              activePlatformId={platformId}
              onSelect={setPlatformId}
            />
          </StatusStickyNav>
          <StatusPlatformList
            platforms={data.platforms}
            categories={data.categories}
            activePlatformId={platformId}
            activeCategoryId={activeCategoryId}
          />
          <Separator />
          {/* <StatusProviderList providers={data.providers} /> */}
          <StatusIncidentList incidents={data.incidents} />
        </>
      )}

      <StatusFooter
        error={error}
        isRefreshing={isRefreshing}
        lastSuccessAt={lastSuccessAt}
      />
    </main>
  )
}

export default StatusPage
